/**
 * InvoiceList page — searchable, sortable, paginated table of all invoices.
 *
 * Requirements: 44.12, 44.13
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { invoiceService, type InvoiceListParams } from '@/services/invoiceService';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import type { InvoiceRecord, SortDirection } from '@/types';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  'Pending Review': 'yellow',
  'Pending VP Approval': 'blue',
  Approved: 'green',
  Paid: 'green',
  Rejected: 'red',
  Disputed: 'red',
};

const PAGE_SIZE = 25;

export function InvoiceList() {
  const [params, setParams] = useState<InvoiceListParams>({
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', params],
    queryFn: () => invoiceService.list(params),
    placeholderData: (prev) => prev,
  });

  const handleSearch = useCallback((query: string) => {
    setParams((prev) => ({ ...prev, search: query || undefined, page: 1 }));
  }, []);

  const handleSort = useCallback((key: string, direction: SortDirection) => {
    setParams((prev) => ({ ...prev, sortBy: key, sortDir: direction, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const columns: Column<InvoiceRecord & Record<string, unknown>>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      sortable: true,
      render: (row) => (
        <Link
          to={`/invoices/${row.invoiceId}`}
          className="text-baker-red no-underline font-medium hover:underline"
        >
          {row.invoiceNumber ?? '—'}
        </Link>
      ),
    },
    {
      key: 'vendorName',
      header: 'Vendor',
      sortable: true,
      render: (row) => (
        <span className="text-text-primary">{row.vendorName ?? '—'}</span>
      ),
    },
    {
      key: 'invoiceDate',
      header: 'Date',
      sortable: true,
      render: (row) => (
        <span className="text-text-secondary tabular-nums">
          {row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      sortable: true,
      render: (row) => (
        <span className="text-text-secondary tabular-nums">
          {row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Total Amount',
      sortable: true,
      render: (row) => (
        <span className="text-text-primary font-medium tabular-nums">
          {row.totalAmount != null
            ? `$${Number(row.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '—'}
        </span>
      ),
    },
    {
      key: 'lifecycleStatus',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <StatusBadge
          label={row.lifecycleStatus}
          variant={STATUS_VARIANTS[row.lifecycleStatus] ?? 'gray'}
        />
      ),
    },
    {
      key: 'codedBy',
      header: 'Coded By',
      render: (row) => (
        <span className="text-text-secondary text-xs">{row.codedBy ?? '—'}</span>
      ),
    },
    {
      key: 'codingSummary',
      header: 'Coding',
      render: (row) => {
        const summary = row.codingSummary as string | null;
        if (!summary) return <span className="text-text-muted text-xs">—</span>;
        const pairs = summary.split(', ');
        return (
          <div className="flex flex-wrap gap-1">
            {pairs.map((pair, i) => (
              <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-surface border border-border-subtle text-text-secondary">
                {pair}
              </span>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <h1 className="text-lg font-semibold text-text-primary mb-5">Invoices</h1>
      <DataTable
        columns={columns}
        data={(data?.data ?? []) as (InvoiceRecord & Record<string, unknown>)[]}
        rowKey={(row) => row.invoiceId}
        totalItems={data?.total}
        page={params.page ?? 1}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
        onSort={handleSort}
        onSearch={handleSearch}
        searchPlaceholder="Search invoices…"
        isLoading={isLoading}
        emptyMessage="No invoices found."
      />
    </div>
  );
}
